import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import React, {useEffect, useId, useRef, useState} from 'react';
import {
  edgeScrollSpeed,
  moveKeyToIndex,
  nearestRow,
  previewOffsets,
  type RowSlot,
} from './chartReorderGeometry';
import './chart-order-list.css';

export interface ChartOrderItem {
  key: string;
  title: string;
}
export interface ChartOrderLabels {
  hint: string;
  keyboardHint: string;
  empty: string;
  drag: (title: string) => string;
  moveUp: (title: string) => string;
  moveDown: (title: string) => string;
  pickedUp: (title: string) => string;
  dropped: (title: string, position: string) => string;
  cancelled: string;
}
export interface ChartOrderListProps {
  items: ChartOrderItem[];
  locale: string;
  disabled: boolean;
  labels: ChartOrderLabels;
  onOrderChange: (keys: string[]) => void;
  onDraggingChange: (dragging: boolean) => void;
  style?: React.CSSProperties;
}
interface PointerDrag {
  key: string;
  title: string;
  id: number;
  startY: number;
  lastY: number;
  node: HTMLElement;
  from: number;
  to: number;
  slots: RowSlot[];
  scrollStart: number;
  started: boolean;
  settling: boolean;
  previousFrame: number;
}

/** Pointer moves update transforms on animation frames, never React state per frame. */
export default function ChartOrderList(props: ChartOrderListProps) {
  const root = useRef<HTMLDivElement>(null),
    rows = useRef(new Map<string, HTMLLIElement>());
  const placeholder = useRef<HTMLLIElement>(null);
  const latest = useRef(props);
  latest.current = props;
  const drag = useRef<PointerDrag | null>(null),
    frame = useRef(0),
    timer = useRef<ReturnType<typeof setTimeout>>();
  const originalKeyboardOrder = useRef<string[] | null>(null);
  const consumedEscape = useRef(false);
  const [activeKey, setActiveKey] = useState<string | null>(null),
    [keyboardKey, setKeyboardKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const hintId = useId();
  const keys = props.items.map(item => item.key),
    fingerprint = JSON.stringify(keys);
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clearVisuals = () =>
    rows.current.forEach(node => {
      node.style.removeProperty('--order-y');
      node.classList.remove('is-settling');
    });
  const stopTimers = () => {
    clearTimeout(timer.current);
    cancelAnimationFrame(frame.current);
  };
  const releaseCapture = (state: PointerDrag) => {
    if (state.node.hasPointerCapture(state.id))
      state.node.releasePointerCapture(state.id);
  };
  const cancel = (announce = true) => {
    const state = drag.current;
    const keyboardActive = originalKeyboardOrder.current !== null;
    drag.current = null;
    stopTimers();
    clearVisuals();
    if (state) releaseCapture(state);
    if (originalKeyboardOrder.current) {
      latest.current.onOrderChange(originalKeyboardOrder.current);
      originalKeyboardOrder.current = null;
    }
    setActiveKey(null);
    setKeyboardKey(null);
    latest.current.onDraggingChange(false);
    if (announce && (state?.started || keyboardActive))
      setAnnouncement(latest.current.labels.cancelled);
  };
  useEffect(() => {
    // Cancel safely if the dialog closes, its size changes, or the window loses focus.
    const abort = () => cancel();
    window.addEventListener('blur', abort);
    window.addEventListener('resize', abort);
    return () => {
      window.removeEventListener('blur', abort);
      window.removeEventListener('resize', abort);
      const state = drag.current;
      drag.current = null;
      stopTimers();
      if (state) releaseCapture(state);
      latest.current.onDraggingChange(false);
    };
  }, []);
  useEffect(() => {
    if (drag.current || props.disabled) cancel(false);
  }, [fingerprint, props.disabled]);

  const draw = (now: number) => {
    const state = drag.current,
      scroll = root.current;
    if (!state?.started || state.settling || !scroll) return;
    const bounds = scroll.getBoundingClientRect();
    const dt = state.previousFrame
      ? Math.min(32, now - state.previousFrame) / 1000
      : 0;
    state.previousFrame = now;
    scroll.scrollTop +=
      edgeScrollSpeed(state.lastY, bounds.top, bounds.bottom) * dt;
    const source = state.slots[state.from],
      last = state.slots[state.slots.length - 1];
    const delta = Math.max(
      -source.top,
      Math.min(
        last.top + last.height - source.top - source.height,
        state.lastY - state.startY + scroll.scrollTop - state.scrollStart,
      ),
    );
    state.to = nearestRow(state.slots, source.top + source.height / 2 + delta);
    const offsets = previewOffsets(state.slots, state.from, state.to);
    if (placeholder.current) {
      placeholder.current.style.transform = `translateY(${
        source.top + offsets[state.from]
      }px)`;
      placeholder.current.style.height = `${source.height}px`;
    }
    latest.current.items.forEach((item, index) =>
      rows.current
        .get(item.key)
        ?.style.setProperty(
          '--order-y',
          `${index === state.from ? delta : offsets[index]}px`,
        ),
    );
    frame.current = requestAnimationFrame(draw);
  };
  const begin = (
    event: React.PointerEvent<HTMLLIElement>,
    item: ChartOrderItem,
    from: number,
  ) => {
    if (
      latest.current.disabled ||
      drag.current ||
      keyboardKey ||
      event.button !== 0 ||
      !event.isPrimary
    )
      return;
    const target = event.target as HTMLElement;
    const handle = target.closest('[data-order-handle]');
    if (
      (target.closest('button') && !handle) ||
      (event.pointerType === 'touch' && !handle)
    )
      return;
    const node = event.currentTarget;
    const state: PointerDrag = {
      key: item.key,
      title: item.title,
      id: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      node,
      from,
      to: from,
      slots: [],
      scrollStart: 0,
      started: false,
      settling: false,
      previousFrame: 0,
    };
    drag.current = state;
    node.setPointerCapture(event.pointerId);
    const activate = () => {
      if (drag.current !== state || !root.current) return;
      state.slots = latest.current.items.map(card => {
        const row = rows.current.get(card.key)!;
        return {top: row.offsetTop, height: row.offsetHeight};
      });
      state.scrollStart = root.current.scrollTop;
      state.started = true;
      setActiveKey(item.key);
      latest.current.onDraggingChange(true);
      setAnnouncement(latest.current.labels.pickedUp(item.title));
      frame.current = requestAnimationFrame(draw);
    };
    // Mouse users can grab the handle directly; touch still requires a hold.
    if (event.pointerType === 'mouse' && handle) activate();
    else timer.current = setTimeout(activate, 300);
  };
  const finish = (event: React.PointerEvent<HTMLLIElement>) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId || state.settling) return;
    clearTimeout(timer.current);
    cancelAnimationFrame(frame.current);
    if (!state.started) {
      cancel(false);
      return;
    }
    state.lastY = event.clientY;
    draw(performance.now());
    cancelAnimationFrame(frame.current);
    state.settling = true;
    const offset = previewOffsets(state.slots, state.from, state.to)[
      state.from
    ];
    state.node.classList.add('is-settling');
    state.node.style.setProperty('--order-y', `${offset}px`);
    const commit = () => {
      if (drag.current !== state) return;
      drag.current = null;
      releaseCapture(state);
      clearVisuals();
      setActiveKey(null);
      latest.current.onOrderChange(
        moveKeyToIndex(
          latest.current.items.map(item => item.key),
          state.key,
          state.to,
        ),
      );
      latest.current.onDraggingChange(false);
      setAnnouncement(
        latest.current.labels.dropped(
          state.title,
          createDisplayNumberFormat(latest.current.locale).format(state.to + 1),
        ),
      );
    };
    timer.current = setTimeout(commit, reduced() ? 0 : 200);
  };
  const step = (item: ChartOrderItem, index: number, direction: -1 | 1) => {
    const next = Math.max(
      0,
      Math.min(props.items.length - 1, index + direction),
    );
    props.onOrderChange(moveKeyToIndex(keys, item.key, next));
    setAnnouncement(
      props.labels.dropped(
        item.title,
        createDisplayNumberFormat(props.locale).format(next + 1),
      ),
    );
  };
  return (
    <div
      className="chart-order"
      ref={root}
      style={props.style}
      onKeyDownCapture={event => {
        if (event.key === 'Escape' && (drag.current || keyboardKey)) {
          event.preventDefault();
          event.stopPropagation();
          consumedEscape.current = true;
          cancel();
        }
      }} onKeyUpCapture={event => {
        // RN Web modals dismiss on keyup, while native HTML dialogs use keydown.
        if (event.key === 'Escape' && consumedEscape.current) {
          event.preventDefault(); event.stopPropagation(); consumedEscape.current = false;
        }
      }}>
      <p className="chart-order__hint" id={hintId}>
        {props.labels.hint}
      </p>
      <ol
        className="chart-order__list"
        aria-describedby={hintId}
        data-dragging={activeKey !== null || undefined}>
        <li
          ref={placeholder}
          className="chart-order__placeholder"
          aria-hidden="true"
        />
        {props.items.map((item, index) => (
          <li
            key={item.key}
            ref={node => {
              if (node) rows.current.set(item.key, node);
              else rows.current.delete(item.key);
            }}
            className={`chart-order__row ${
              activeKey === item.key || keyboardKey === item.key
                ? 'is-active'
                : ''
            }`}
            onPointerDown={event => begin(event, item, index)}
            onPointerMove={event => {
              const state = drag.current;
              if (state?.id !== event.pointerId || state.settling) return;
              state.lastY = event.clientY;
              if (!state.started && Math.abs(state.lastY - state.startY) > 8)
                cancel(false);
              if (state.started) event.preventDefault();
            }}
            onPointerUp={finish}
            onPointerCancel={() => cancel()}
            onLostPointerCapture={() => {
              if (drag.current && !drag.current.settling) cancel();
            }}
            onContextMenu={event => event.preventDefault()}>
            <span className="chart-order__index">
              {createDisplayNumberFormat(props.locale).format(index + 1)}
            </span>
            <strong>{item.title}</strong>
            <button
              type="button"
              className="chart-order__grab"
              data-order-handle
              aria-label={props.labels.drag(item.title)}
              aria-describedby={`${hintId} ${hintId}-keyboard`}
              aria-pressed={activeKey === item.key || keyboardKey === item.key}
              disabled={props.disabled}
              onKeyDown={event => {
                if (event.key === ' ' || event.key === 'Enter') {
                  event.preventDefault();
                  if (keyboardKey === item.key) {
                    originalKeyboardOrder.current = null;
                    setKeyboardKey(null);
                    props.onDraggingChange(false);
                    setAnnouncement(
                      props.labels.dropped(
                        item.title,
                        createDisplayNumberFormat(props.locale).format(index + 1),
                      ),
                    );
                  } else {
                    originalKeyboardOrder.current = keys;
                    setKeyboardKey(item.key);
                    props.onDraggingChange(true);
                    setAnnouncement(props.labels.pickedUp(item.title));
                  }
                } else if (
                  keyboardKey === item.key &&
                  (event.key === 'ArrowUp' || event.key === 'ArrowDown')
                ) {
                  event.preventDefault();
                  step(item, index, event.key === 'ArrowUp' ? -1 : 1);
                }
              }}>
              <svg
                viewBox="0 0 20 24"
                width="20"
                height="24"
                aria-hidden="true"
                fill="currentColor">
                {[6, 12, 18].flatMap(y =>
                  [7, 13].map(x => (
                    <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" />
                  )),
                )}
              </svg>
            </button>
            {([-1, 1] as const).map(direction => (
              <button
                type="button"
                className="chart-order__step"
                key={direction}
                aria-label={
                  direction === -1
                    ? props.labels.moveUp(item.title)
                    : props.labels.moveDown(item.title)
                }
                disabled={
                  props.disabled ||
                  activeKey !== null ||
                  keyboardKey !== null ||
                  (direction === -1
                    ? index === 0
                    : index === props.items.length - 1)
                }
                onClick={() => step(item, index, direction)}>
                <svg
                  viewBox="0 0 20 20"
                  width="17"
                  height="17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  aria-hidden="true">
                  <path
                    d={direction === -1 ? 'm5 12 5-5 5 5' : 'm5 8 5 5 5-5'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ))}
          </li>
        ))}
      </ol>
      <span className="chart-order__sr" id={`${hintId}-keyboard`}>
        {props.labels.keyboardHint}
      </span>
      {!props.items.length && (
        <p className="chart-order__empty">{props.labels.empty}</p>
      )}
      <span
        className="chart-order__sr"
        role="status"
        aria-live="polite"
        aria-atomic="true">
        {announcement}
      </span>
    </div>
  );
}
